import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { UserActivityStatus } from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import isValidUser from '../../lib/isValidUser';
import log from '../../lib/log';
import requireUser from '../../lib/requireUser';
import {
  ServiceUserUtils,
  userStatsLastActionTimeFix,
} from '../../lib/serviceUserUtils';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';

/**
 * Agent finished handling a non-queue (external) call. The post-call
 * availability state from RTCS (e.g. 'Ready', 'Away') is not in the activity
 * vocabulary, so `setEventActivityStatus` falls back to `None`, which is the
 * correct activity after the call ends. The WrapUp guard mirrors
 * `handleAgentSessionTerminated`: if a wrapup interval has already begun for an
 * overlapping queue session, the wrapup event is authoritative for activity.
 */
async function handleAgentExternalCallEnded(
  event: Telecom.AgentExternalCallEndedEvent,
) {
  if (
    !FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureHandleExternalCallEvents,
    )
  ) {
    return;
  }

  trackRTCSEventLatency(
    event.timestamp,
    RTCSEventType.AGENT_EXTERNAL_CALL_ENDED,
  );

  if (!isValidUser(event.agentId)) {
    return;
  }

  const user = await requireUser(event.agentId);
  log('rtcs:agent:external_call_ended:' + user?.name, event);

  user.stats = userStatsFromAgentStats(event.stats);
  userStatsLastActionTimeFix({ user, timestamp: event.timestamp });

  if (ServiceUserUtils.getActivityStatus(user) !== UserActivityStatus.WrapUp) {
    ServiceUserUtils.setEventActivityStatus(user, event.availabilityState);
  }
}

export default handleAgentExternalCallEnded;
